import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MESSAGES } from '@/lib/messages';
import { captureException } from '@/lib/sentry';

type Props = {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
};

type State = { error: Error | null };

/**
 * Class-based React error boundary. We can't use a function component
 * here because error boundaries require `componentDidCatch` /
 * `getDerivedStateFromError` lifecycle methods.
 *
 * This class is wrapped by the exported `<ErrorBoundary>` which adds
 * the React Query cache reset on recover.
 */
export class ErrorBoundaryBase extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] caught error', error, info);
    captureException(error, { extra: { componentStack: info?.componentStack } });
    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return <DefaultErrorFallback error={error} reset={this.reset} />;
    }
    return this.props.children;
  }
}
const DefaultErrorFallback = ({ error, reset }: { error: Error; reset: () => void }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
    <div className="space-y-1">
      <h1 className="font-serif text-2xl font-semibold text-fg-base">Une erreur est survenue</h1>
      <p className="max-w-md text-sm text-fg-subtle">{error.message || MESSAGES.errors.appError}</p>
    </div>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={reset}
        className="rounded-md bg-burgundy-600 px-4 py-2 text-sm font-medium text-fg-inverse transition-colors hover:bg-burgundy-700"
      >
        Réessayer
      </button>
      <button
        type="button"
        onClick={() => window.location.assign('/')}
        className="rounded-md border border-border-base bg-surface-base px-4 py-2 text-sm transition-colors hover:bg-surface-muted"
      >
        Retour à l'accueil
      </button>
    </div>
  </div>
);

/**
 * Public error boundary. On reset, also clears the React Query cache
 * so that any error originating from a query is wiped before the next
 * render.
 */
export const ErrorBoundary = ({ children, fallback, onError }: Props) => {
  const queryClient = useQueryClient();
  return (
    <ErrorBoundaryBase
      fallback={(error, reset) => {
        const onReset = () => {
          queryClient.resetQueries();
          reset();
        };
        if (fallback) return fallback(error, onReset);
        return <DefaultErrorFallback error={error} reset={onReset} />;
      }}
      onError={onError}
    >
      {children}
    </ErrorBoundaryBase>
  );
};
